import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/NewButton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { backendClient } from '@/lib/api-client';
import type { Matter } from '@/types/base';
import { FancyMultiSelect, type Options } from '@/components/ui/fancy-multi-select';
import useAllUsers from '@/hooks/useAllUsers';
import { fullName } from '@/lib/user';
import useMatterMembers from '@/hooks/useMatterMembers';

// Modified schema to handle multi-select users
const formSchema = z.object({
  users: z.array(z.object({
    value: z.string(),
    label: z.string()
  })).min(1, { message: 'Please select at least one user' }),
  role: z.string().min(1, { message: 'Role is required' }),
});

type FormValues = z.infer<typeof formSchema>;

const AddMember = ({ matter, onSuccess }: { matter: Matter; onSuccess: () => void }) => {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { users, isLoading: isLoadingUsers } = useAllUsers();
  const { members } = useMatterMembers(matter.guid);
  const [userOptions, setUserOptions] = useState<Options[]>([]);

  // Define available roles - only Member and Owner
  const availableRoles = [
    { name: 'Member' },
    { name: 'Owner' }
  ];

  // Create a form with default values
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      users: [],
      role: 'Member',
    },
  });

  // Prepare user options for multi-select dropdown
  useEffect(() => {
    if (users && users.length > 0 && members) {
      // Filter out users who are already members of this matter
      const existingMemberGuids = new Set(members.map(member => member.user_guid));

      const filteredUsers = users.filter(user =>
        !existingMemberGuids.has(user.guid)
      );

      // Convert to Options format required by multi-select
      const options = filteredUsers.map(user => ({
        value: user.email,
        label: `${fullName(user)} (${user.email})`
      }));

      setUserOptions(options);
    }
  }, [users, members]);

  const onSubmit = async (values: FormValues) => {
    if (values.users.length === 0) {
      toast.error(t('select-at-least-one-user'));
      return;
    }

    setIsSubmitting(true);
    try {
      // Create an array of promises for each user to add
      const addPromises = values.users.map(user =>
        backendClient.post(`/matter/${matter.guid}/members`, {
          email: user.value, // The email is stored in the value field
          role: values.role,
        })
      );

      // Wait for all requests to complete
      await Promise.all(addPromises);

      toast.success(values.users.length > 1
        ? t('members-added', { count: values.users.length })
        : t('member-added')
      );

      form.reset();
      setIsOpen(false);
      onSuccess();
    } catch (error) {
      console.error('Error adding members:', error);
      toast.error(t('error-adding-member'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      form.reset();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button text={t('add-member')} />
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>{t('add-member-to-matter')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="users"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="md:block md:pb-0.5">{t('select-users')}</FormLabel>
                  <FormControl>
                    <div onClick={(e) => e.stopPropagation()}>
                      <FancyMultiSelect
                        placeholder={t('search-users')}
                        options={userOptions}
                        value={field.value}
                        onValueChange={field.onChange}
                        expandOnFocus={false}
                      />
                    </div>
                  </FormControl>
                  <FormDescription>
                    {isLoadingUsers
                      ? t('loading-users')
                      : t('available-users', { count: userOptions.length })}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="md:block md:pb-0.5">{t('role')}</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('select-role')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableRoles.map((role) => (
                        <SelectItem key={role.name} value={role.name}>
                          {role.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {field.value === 'Owner'
                      ? t('owner-role-description')
                      : t('member-role-description')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end space-x-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                text={t('cancel')}
              />
              <Button type="submit" loading={isSubmitting} loadingText={t('adding')} text={t('add')} />
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddMember;
